/**
 * Elevio Help Center Integration
 * Loads and configures the Elevio SDK for in-app help and documentation
 */
import config from '@/config';

import { logger } from '@/utils/logger';

interface ElevioSettings {
  docked_position?: string;
  side?: string;
  loggedin_only?: boolean;
}

interface ElevioTranslations {
  [key: string]: string;
}

interface ElevioInstance {
  setSettings: (settings: ElevioSettings) => void;
  setTranslations: (translations: ElevioTranslations) => void;
  setUser: (user: { email: string; user_hash: string }) => void;
  open: () => void;
  close: () => void;
  openArticle: (articleId: number) => void;
  openCategory: (categoryId: number) => void;
}

declare global {
  interface Window {
    _elev?: {
      on: (event: string, callback: (elevio: ElevioInstance) => void) => void;
      setSettings: (settings: ElevioSettings) => void;
      setTranslations: (translations: ElevioTranslations) => void;
      setUser: (user: { email: string; user_hash: string }) => void;
      open?: () => void;
      close?: () => void;
      openArticle?: (articleId: number) => void;
      openCategory?: (categoryId: number) => void;
      account_id?: string;
      q?: Array<[string, (elevio: ElevioInstance) => void]>;
    };
  }
}

/**
 * Initialize Elevio SDK
 * Must be called after user authentication
 */
export const initElevio = async (userEmail: string, getElevioHash: () => Promise<string>) => {
  // Only run in browser environment
  if (typeof window === 'undefined') return;

  const { ELEVIO_ACCOUNT_ID } = config;
  if (!ELEVIO_ACCOUNT_ID) {
    logger.warn('Elevio Account ID not configured');
    return;
  }

  // Initialize Elevio object
  if (!window._elev) {
    (function (e: Window, l: Document, v: string, i: string, o: string, n: string) {
      const windowWithElev = e as Window &
        Record<string, { account_id?: string; q?: unknown[]; on?: unknown }>;
      if (!windowWithElev[i]) {
        windowWithElev[i] = {};
      }
      const elev = windowWithElev[i];
      elev.account_id = n;
      const g = l.createElement(v) as HTMLScriptElement;
      g.type = 'text/javascript';
      g.async = true;
      g.src = o + n;
      const h = l.getElementsByTagName(v)[0];
      h.parentNode?.insertBefore(g, h);
      elev.q = [];
      elev.on = function (z: string, y: (elevio: ElevioInstance) => void) {
        elev.q?.push([z, y]);
      };
    })(
      window,
      document,
      'script',
      '_elev',
      'https://cdn.elev.io/sdk/bootloader/v4/elevio-bootloader.js?cid=',
      ELEVIO_ACCOUNT_ID
    );
  }

  // Configure Elevio when loaded
  window._elev!.on('load', (elev: ElevioInstance) => {
    elev.setSettings({
      docked_position: 'floor',
      side: 'right',
      loggedin_only: true,
    });

    elev.setTranslations({
      search: 'What would you like to do today?',
    });

    // Set user with email hash
    getElevioHash()
      .then((userHash) => {
        elev.setUser({
          email: userEmail,
          user_hash: userHash,
        });
      })
      .catch((err) => {
        logger.warn('Failed to set Elevio user:', err);
      });
  });
};

/**
 * Logout user from Elevio (call on app logout to clear user context)
 */
export const logoutElevio = () => {
  const elev = window._elev as unknown as { logoutUser?: () => void } | undefined;
  if (elev?.logoutUser) {
    elev.logoutUser();
  }
};

/**
 * Open Elevio to a specific article or category
 */
export const openElevioArticle = (articleId: string) => {
  if (window._elev) {
    window._elev.on('load', (elev: ElevioInstance) => {
      elev.openArticle(Number(articleId));
    });
  }
};

/**
 * Open Elevio to a specific category
 */
export const openElevioCategory = (categoryId: string) => {
  if (window._elev) {
    window._elev.on('load', (elev: ElevioInstance) => {
      elev.openCategory(Number(categoryId));
    });
  }
};
